import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDrawer } from '../context/DrawerContext';
import { ScrollContainerContext } from '../context/ScrollContainerContext';
import { CLOSE_METHOD } from '../utils/trackEvent';
import {
	getNavItemKey,
	isVisibleNavItem,
	isGroupHeading,
	resolveZonePosition
} from '../utils/navItemUtils';
import PanelHeader from './PanelHeader';
import DrilldownGroup from './DrilldownGroup';
import DrilldownLeafLink from './DrilldownLeafLink';
import DrawerNavFragment from './DrawerNavFragment';
import DrilldownBackButton from './DrilldownBackButton';

/**
 * SubMenuPanel — Level 2 drilldown content for one visited parent item.
 *
 * The drilldown keeps one SubMenuPanel mounted per visited item (see
 * DrilldownPanel) and shows only the active one, so switching between items
 * never remounts a body and its embedded fragment never re-bootstraps. Only
 * the active body is visible; inactive bodies are hidden via a class (so
 * Bootstrap's `.d-flex !important` can't override an inline `display`) + `inert`.
 *
 * Each body owns its own scroll container and provides it via
 * ScrollContainerContext, so its fragments observe against their own container
 * rather than a single shared ref clobbered by the last body to render.
 *
 * Positioning, slide animation, and the focus trap are owned by the SlidePanel
 * container in DrilldownPanel, not this component.
 *
 * @param {SubMenuPanelProps} props
 * @param {NavigationItem|null} props.activeItem - The parent item this body renders.
 * @param {() => void} props.onSubmenuClose - Called when the back button is clicked.
 * @param {boolean} [props.isActive] - Whether this body is the visible one.
 *   When false the body is hidden (via the --hidden class) and inert.
 * @param {ElementRef} [props.backRef] - Ref forwarded to the back button element.
 */
const SubMenuPanel = ({
	activeItem = null,
	onSubmenuClose,
	isActive = true,
	backRef = null
}) => {
	const { closeDrawer } = useDrawer();
	const scrollContainerRef = useRef(null);

	// Matches NavItem: a followed sub-nav link attributes its own drawer close.
	const closeOnNavLink = useCallback(
		() => closeDrawer(CLOSE_METHOD.NAV_LINK),
		[closeDrawer]
	);

	const children =
		activeItem && activeItem.navigationItems
			? activeItem.navigationItems.filter(
					(child) =>
						isVisibleNavItem(child) ||
						child.embedded ||
						isGroupHeading(child)
			  )
			: [];

	return (
		<ScrollContainerContext.Provider value={scrollContainerRef}>
			<div
				// Visibility is class-driven, NOT an inline `display` toggle:
				// Bootstrap's `.d-flex` is `display: flex !important` (host-provided
				// utility), which overrides a non-important inline `display: none`,
				// so inactive bodies never hid and stacked. Instead apply `d-flex`
				// only when active and a plain hidden class otherwise — with d-flex
				// absent, `display: none` wins cleanly.
				className={`ws-nav-drawer__drilldown-panel flex-column flex-grow-1 ${
					isActive
						? 'd-flex'
						: 'ws-nav-drawer__drilldown-panel--hidden'
				}`}
				style={{ minHeight: 0 }}
				// Inactive bodies are removed from the tab order + a11y tree with
				// `inert` (not aria-hidden) so the shared focus trap only ever sees
				// the active body's focusable elements.
				inert={isActive ? undefined : ''}
				data-testid="drilldown-body"
			>
				{/* Level 2 header row */}
				<div
					className="ws-nav-drawer__panel-header-outer py-4"
					data-testid="drilldown-header"
				>
					<PanelHeader
						leftAction={
							<DrilldownBackButton
								backRef={backRef}
								onSubmenuClose={onSubmenuClose}
								categoryLabel={activeItem?.label}
							/>
						}
					/>
				</div>

				{/* Child content — grouped or ungrouped */}
				{/* Outer scroll container. role="region" + aria-label preserve the
				    accessible landmark that was previously carried by the <ul>. */}
				<div
					ref={scrollContainerRef}
					role="region"
					aria-label={activeItem?.label}
					className="overflow-auto flex-grow-1 flex-column align-items-start align-self-stretch"
				>
					<ul className="ws-nav-drawer__drilldown-list list-unstyled m-0">
						{children.map((child, index) =>
							// eslint-disable-next-line no-nested-ternary
							child.embedded ? (
								<li
									key={getNavItemKey(child, index)}
									className="ws-nav-drawer__fragment"
								>
									<DrawerNavFragment
										url={child.url}
										zonePosition={resolveZonePosition(
											children,
											index
										)}
									/>
								</li>
							) : isGroupHeading(child) ? (
								// Fragment wrappers reach this branch too (url '#'
								// with children), and their zones are the group's
								// only content — so the group needs this list's
								// verdict on its position, not a hardcoded 'below'.
								<DrilldownGroup
									key={getNavItemKey(child, index)}
									group={child}
									onLinkClick={closeOnNavLink}
									zonePosition={resolveZonePosition(
										children,
										index
									)}
								/>
							) : (
								<DrilldownLeafLink
									key={getNavItemKey(child, index)}
									item={child}
									onLinkClick={closeOnNavLink}
								/>
							)
						)}
					</ul>
				</div>
			</div>
		</ScrollContainerContext.Provider>
	);
};

SubMenuPanel.propTypes = {
	activeItem: PropTypes.shape({
		label: PropTypes.string.isRequired,
		navigationItems: PropTypes.arrayOf(PropTypes.shape({}))
	}),
	onSubmenuClose: PropTypes.func.isRequired,
	isActive: PropTypes.bool,
	backRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
};

export default SubMenuPanel;
